import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, ZoomIn, ZoomOut, X, Check } from "lucide-react";

const VIEW = 288; // on-screen square viewport (px)
const OUT = 512; // exported square size (px)

/**
 * Square avatar cropper: drag to reposition, slider/buttons to zoom, live square preview.
 * Calls onCrop(blob) with a 512x512 JPEG of the visible square. Fully client-side.
 */
export default function AvatarCropper({ file, onCancel, onCrop, busy }) {
  const [imgEl, setImgEl] = useState(null);
  const [base, setBase] = useState(1); // scale that makes the image cover the viewport
  const [zoom, setZoom] = useState(1); // user zoom multiplier (1..3)
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const b = VIEW / Math.min(img.naturalWidth, img.naturalHeight);
      setImgEl(img);
      setBase(b);
      setZoom(1);
      // center
      const dispW = img.naturalWidth * b;
      const dispH = img.naturalHeight * b;
      setOffset({ x: (VIEW - dispW) / 2, y: (VIEW - dispH) / 2 });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const clamp = useCallback(
    (o, z) => {
      if (!imgEl) return o;
      const dispW = imgEl.naturalWidth * base * z;
      const dispH = imgEl.naturalHeight * base * z;
      return {
        x: Math.min(0, Math.max(VIEW - dispW, o.x)),
        y: Math.min(0, Math.max(VIEW - dispH, o.y)),
      };
    },
    [imgEl, base]
  );

  const applyZoom = (z) => {
    const nz = Math.min(3, Math.max(1, z));
    // keep viewport center stable while zooming
    const cx = VIEW / 2, cy = VIEW / 2;
    setOffset((o) => {
      const ratio = nz / zoom;
      const nx = cx - (cx - o.x) * ratio;
      const ny = cy - (cy - o.y) * ratio;
      return clamp({ x: nx, y: ny }, nz);
    });
    setZoom(nz);
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const nx = drag.current.ox + (e.clientX - drag.current.sx);
    const ny = drag.current.oy + (e.clientY - drag.current.sy);
    setOffset(clamp({ x: nx, y: ny }, zoom));
  };
  const onPointerUp = () => { drag.current = null; };

  const confirm = () => {
    if (!imgEl) return;
    const s = base * zoom;
    const cropNat = VIEW / s;
    const x0 = -offset.x / s;
    const y0 = -offset.y / s;
    const canvas = document.createElement("canvas");
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, OUT, OUT);
    ctx.drawImage(imgEl, x0, y0, cropNat, cropNat, 0, 0, OUT, OUT);
    canvas.toBlob((blob) => { if (blob) onCrop(blob); }, "image/jpeg", 0.92);
  };

  const dispW = imgEl ? imgEl.naturalWidth * base * zoom : 0;
  const dispH = imgEl ? imgEl.naturalHeight * base * zoom : 0;

  return createPortal(
    <div data-testid="avatar-cropper" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white border border-[var(--border)] shadow-2xl p-5 fade-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-base">Ritaglia la foto</h3>
          <button data-testid="cropper-close-btn" onClick={onCancel} className="text-[var(--muted)] hover:text-black transition-colors"><X size={18} /></button>
        </div>

        <div
          className="relative mx-auto overflow-hidden rounded-full border border-[var(--border)] bg-slate-100 cursor-grab active:cursor-grabbing select-none touch-none"
          style={{ width: VIEW, height: VIEW }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          data-testid="cropper-viewport"
        >
          {imgEl && (
            <img
              src={imgEl.src}
              alt=""
              draggable={false}
              className="absolute max-w-none pointer-events-none"
              style={{ left: offset.x, top: offset.y, width: dispW, height: dispH }}
            />
          )}
          <div className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-[#FC82ED]/70" />
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button data-testid="cropper-zoom-out" onClick={() => applyZoom(zoom - 0.2)} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-[var(--muted)] hover:text-black transition-colors"><ZoomOut size={16} /></button>
          <input
            data-testid="cropper-zoom"
            type="range" min="1" max="3" step="0.01" value={zoom}
            onChange={(e) => applyZoom(parseFloat(e.target.value))}
            className="flex-1 accent-[#FC82ED]"
          />
          <button data-testid="cropper-zoom-in" onClick={() => applyZoom(zoom + 0.2)} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-[var(--muted)] hover:text-black transition-colors"><ZoomIn size={16} /></button>
        </div>

        <p className="text-[11px] text-[var(--muted)] mt-3 text-center">Trascina per spostare, usa lo zoom per ingrandire. L'anteprima mostra il ritaglio quadrato.</p>

        <div className="flex items-center gap-3 mt-4">
          <button data-testid="cropper-cancel-btn" onClick={onCancel} disabled={busy}
                  className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-[var(--muted)] hover:text-black transition-colors disabled:opacity-60">Annulla</button>
          <button data-testid="cropper-confirm-btn" onClick={confirm} disabled={busy || !imgEl}
                  className="flex-1 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Salva foto
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
